import type { OpenSource } from '@parcel/analytics';
import { capture } from '@parcel/analytics/browser';
import { Badge } from '@parcel/ui';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { Building2, MapPin, Users } from 'lucide-react';
import * as React from 'react';
import { accountWebsite } from '../../lib/account-website.js';
import { primaryAccountType } from '../../lib/accounts.js';
import { useCaptureOnce } from '../../lib/analytics.js';
import { type ApiProjectContact, useProjectContacts } from '../../lib/contacts.js';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import { buildReportContext } from '../../lib/feedback.js';
import { logoUrl } from '../../lib/logo.js';
import { type AccountRow, KIND_META, MOCK_ACCOUNTS } from '../../lib/mock-accounts.js';
import {
  type ProjectRow,
  formatDetected,
  formatSqft,
  stageMeta,
} from '../../lib/projects-display.js';
import {
  type ApiProject,
  type ApiProjectAccountRole,
  type ApiProjectAccountSubResource,
  type ApiProjectAccountType,
  type ApiProjectImage,
  apiProjectToRow,
  useProjectAccounts,
  useProjectDetail,
  useProjectDocuments,
} from '../../lib/projects.js';
import { useProjectSignals, useSignalScope } from '../../lib/signals.js';
import { isSupabaseConfigured } from '../../lib/supabase.js';
import { useTheme } from '../../lib/theme.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../../lib/workspaces.js';
import { ImageCacheProvider } from '../auth/authenticated-image.js';
import { ContactAvatar } from '../contacts/contact-avatar.js';
import { ContactRoleBadges } from '../contacts/contact-role-badges.js';
import {
  CoverSkeleton,
  ListSkeleton,
  SectionSkeleton,
  TextSkeleton,
} from '../entity-drawer/detail-skeletons.js';
import {
  EntityDetailLayout,
  type RecordDetailChromeProps,
} from '../entity-drawer/entity-detail-layout.js';
import {
  DetailFieldGrid,
  EmptyTabState,
  PropertySection,
} from '../entity-drawer/entity-drawer-sections.js';
import { EntityAvatar, EntityDrawerHeader } from '../entity-drawer/entity-drawer-shell.js';
import {
  type EvidenceRowItem,
  ExpandableEvidenceList,
} from '../entity-drawer/expandable-evidence-list.js';
import { ExpandableText } from '../entity-drawer/expandable-text.js';
import { CitedValue } from '../entity-drawer/field-evidence-link.js';
import { getProjectTypeIcon } from '../entity-drawer/project-type-icon.js';
import { SignalFeed } from '../entity-drawer/signal-feed.js';
import { SignalScopeToggle } from '../entity-drawer/signal-scope-toggle.js';
import { ReportDataIssueDialog, ReportIssueButton } from '../feedback/report-data-issue-dialog.js';
import { WatchRecordButton } from '../watch/watch-record-button.js';
import { ImageLightbox } from './image-lightbox.js';
import { LifecycleRail } from './lifecycle-rail.js';
import { ProjectDataSection } from './project-data-section.js';
import { ProjectDocumentsTab } from './project-documents-tab.js';
import { ProjectHero } from './project-hero.js';
import { ProjectPhotosGrid } from './project-photos-grid.js';
import { StatusBadge } from './status-badge.js';

interface ProjectDetailProps extends RecordDetailChromeProps {
  projectId: string;
  seedProject?: ProjectRow | null;
  source: OpenSource;
}

export const PROJECT_DETAIL_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'signals', label: 'Signals' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'contacts', label: 'Contacts' },
  { value: 'documents', label: 'Documents' },
];

export function ProjectDetail({
  projectId,
  seedProject,
  source,
  mode,
  onToggleMode,
  backLabel,
  onBack,
  onPrev,
  onNext,
  onNavigate,
}: ProjectDetailProps) {
  const [reportOpen, setReportOpen] = React.useState(false);
  const { resolvedTheme } = useTheme();

  useCaptureOnce(projectId, (id) => capture('project_opened', { project_id: id, source }));

  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id;
  const [signalScope, setSignalScope] = useSignalScope(projectId);
  const detailQuery = useProjectDetail(projectId, workspaceId);
  const accountsQuery = useProjectAccounts(projectId, workspaceId);
  const signalsQuery = useProjectSignals(projectId, workspaceId, signalScope);
  // The rail's evidence (which reached rungs show a direct signal + date + source link vs
  // "(implied)") must reflect ALL canonical signals, consistent with the stage itself being
  // derived from all signals. Keeping a separate 'all'-scoped query ensures the rail stays
  // accurate regardless of the user's chosen scope on the Signals tab. When the user
  // switches the Signals tab to 'all', the scoped query's cache key matches this one and
  // react-query shares the result (no double fetch). At the default 'subscribed' scope,
  // this is one extra request — the cost of accurate rail evidence.
  const railSignalsQuery = useProjectSignals(projectId, workspaceId, 'all');
  const contactsQuery = useProjectContacts(projectId, workspaceId);
  const documentsQuery = useProjectDocuments(projectId, workspaceId);

  const apiProject = detailQuery.data?.project ?? null;
  const accounts: ApiProjectAccountSubResource[] = accountsQuery.data?.accounts ?? [];
  const signals = signalsQuery.data?.signals ?? [];
  const railSignals = railSignalsQuery.data?.signals ?? [];
  const contacts: ApiProjectContact[] = contactsQuery.data?.contacts ?? [];
  const documentPages = documentsQuery.data?.pages ?? [];
  const documents = documentPages.flatMap((page) => page.documents);
  const hasRealDetail = Boolean(apiProject);
  // A query waiting on the workspace id is still loading, not empty.
  const pending = (query: { isLoading: boolean }) => workspaceQuery.isLoading || query.isLoading;
  const detailPending = !apiProject && pending(detailQuery);

  // One image set for the cover, the Photos tab and the lightbox: primary first, failed ones dropped.
  const [imageState, dispatchImage] = React.useReducer(imageStateReducer, EMPTY_IMAGE_STATE);
  const [imagesOf, setImagesOf] = React.useState(projectId);
  if (imagesOf !== projectId) {
    setImagesOf(projectId);
    dispatchImage({ type: 'reset' });
  }
  const projectMedia = apiProject?.media;
  const orderedImages = React.useMemo<ApiProjectImage[]>(() => {
    if (!projectMedia) return [];
    const primary = projectMedia.primary_image;
    return primary
      ? [primary, ...projectMedia.images.filter((image) => image.id !== primary.id)]
      : projectMedia.images;
  }, [projectMedia]);
  const visibleImages = React.useMemo(
    () => orderedImages.filter((image) => !imageState.failed.has(image.id)),
    [orderedImages, imageState.failed],
  );
  const lightboxIndex = visibleImages.findIndex((image) => image.id === imageState.open);
  const markImageFailed = React.useCallback(
    (id: string) => dispatchImage({ type: 'fail', id, ordered: orderedImages }),
    [orderedImages],
  );
  const setLightboxId = React.useCallback(
    (id: string | null) => dispatchImage({ type: 'open', id }),
    [],
  );
  // The cover or tile that opened the lightbox, so focus returns to it on close.
  const [lightboxOpener, setLightboxOpener] = React.useState<HTMLElement | null>(null);
  const openImage = React.useCallback(
    (id: string, opener?: HTMLElement) => {
      setLightboxOpener(opener ?? null);
      setLightboxId(id);
    },
    [setLightboxId],
  );
  // Facts and team land together, so the overview fills in once instead of section by section.
  const overviewPending = detailPending || pending(accountsQuery);
  const displayProject = React.useMemo(
    () => projectDisplayRow(seedProject, apiProject),
    [seedProject, apiProject],
  );
  const projectDescription = formatProjectDescription(apiProject?.description);

  const developerLabel = React.useMemo(
    () => formatAccountNames(accounts, 'developer') || displayProject?.developer || '—',
    [accounts, displayProject?.developer],
  );
  const ownerLabel = React.useMemo(() => formatAccountNames(accounts, 'owner') || '—', [accounts]);
  const architectLabel = React.useMemo(
    () => formatAccountNames(accounts, 'architect') || displayProject?.architect || '—',
    [accounts, displayProject?.architect],
  );
  const contractorLabel = React.useMemo(
    () => formatAccountNames(accounts, 'gc') || '—',
    [accounts],
  );
  const evidence = apiProject?.field_evidence ?? {};

  const accountRows = React.useMemo<EvidenceRowItem[]>(() => {
    if (!displayProject) return [];
    if (hasRealDetail) {
      return accounts.map((account) => ({
        key: account.id,
        entityKind: 'account',
        entityId: account.id,
        projectId: displayProject.id,
        entityName: account.name,
        avatar: (
          <EntityAvatar
            src={logoUrl(accountWebsite(account))}
            alt={account.name}
            fallbackIcon={Building2}
            size="sm"
            href={normalizeExternalUrl(accountWebsite(account))}
          />
        ),
        title: account.name,
        subtitle:
          humanizeProjectRoles(account.effective_roles ?? []) || humanizeRoles(account.roles),
        openLabel: 'Open account',
        openIcon: Building2,
        onOpen: () =>
          onNavigate?.(
            { type: 'account', id: account.id },
            { seed: apiAccountToSeedRow(account), fromLabel: displayProject.name },
          ),
      }));
    }
    const names = new Set([displayProject.developer, displayProject.architect].filter(Boolean));
    return MOCK_ACCOUNTS.filter((a) => names.has(a.name)).map((a) => ({
      key: a.id,
      entityKind: 'account' as const,
      entityId: a.id,
      projectId: displayProject.id,
      entityName: a.name,
      avatar: (
        <EntityAvatar
          src={logoUrl(accountWebsite(a))}
          alt={a.name}
          fallbackIcon={Building2}
          size="sm"
          href={normalizeExternalUrl(accountWebsite(a))}
        />
      ),
      title: a.name,
      subtitle: KIND_META[a.kind].label,
      openLabel: 'Open account',
      openIcon: Building2,
      onOpen: () =>
        onNavigate?.({ type: 'account', id: a.id }, { seed: a, fromLabel: displayProject.name }),
    }));
  }, [displayProject, hasRealDetail, accounts, onNavigate]);

  const contactRows = React.useMemo<EvidenceRowItem[]>(() => {
    if (!displayProject) return [];
    return contacts.map((c) => {
      const titleText = c.title ?? c.email ?? null;
      return {
        key: c.id,
        entityKind: 'contact' as const,
        entityId: c.id,
        projectId: displayProject.id,
        entityName: c.name,
        avatar: (
          <ContactAvatar
            name={c.name}
            accountWebsite={c.account_website}
            email={c.email}
            size="sm"
          />
        ),
        title: c.name,
        subtitle: titleText ? (
          <span className="ph-no-capture text-muted-foreground">{titleText}</span>
        ) : (
          '—'
        ),
        meta: c.roles.length ? <ContactRoleBadges roles={c.roles} empty={null} /> : null,
        openLabel: 'Open contact',
        openIcon: Users,
        onOpen: () =>
          onNavigate?.({ type: 'contact', id: c.id }, { fromLabel: displayProject.name }),
      };
    });
  }, [displayProject, contacts, onNavigate]);

  const columns = mode === 'expanded' ? 1 : 2;
  const coverSize = mode === 'expanded' ? 'large' : 'compact';
  const summarySkeleton = (
    <>
      <SectionSkeleton rows={5} columns={columns} />
      <SectionSkeleton rows={3} columns={columns} />
      <SectionSkeleton rows={4} columns={columns} />
    </>
  );

  if (!displayProject) {
    // Header keeps close, back, and prev/next reachable while loading or after a failed load.
    const header = (
      <EntityDrawerHeader
        eyebrow="Project details"
        title={detailQuery.isError ? 'Unable to load project' : 'Loading project…'}
        metaIcon={MapPin}
        metaText=""
        entityIcon={<EntityAvatar alt="" fallbackIcon={getProjectTypeIcon('other')} />}
        onPrev={onPrev}
        onNext={onNext}
        mode={mode}
        onToggleMode={onToggleMode}
        backLabel={backLabel}
        onBack={onBack}
        loading={!detailQuery.isError}
      />
    );
    if (detailQuery.isError) return header;
    return (
      <EntityDetailLayout
        mode={mode}
        header={header}
        lead={<CoverSkeleton size={coverSize} />}
        summary={
          <>
            {mode === 'expanded' ? <TextSkeleton /> : null}
            {summarySkeleton}
          </>
        }
        tabs={PROJECT_DETAIL_TABS.map((tab) => ({
          ...tab,
          drawerOnly: tab.value === 'overview',
          content: tab.value === 'overview' ? <TextSkeleton /> : <ListSkeleton />,
        }))}
      />
    );
  }

  const summarySections = overviewPending ? (
    summarySkeleton
  ) : (
    <>
      <PropertySection title="Details">
        <DetailFieldGrid
          columns={columns}
          fields={[
            {
              label: 'Location',
              value: `${displayProject.city}, ${displayProject.state}`,
            },
            {
              label: 'Size',
              value: (
                <CitedValue evidence={evidence.gross_floor_area_sf}>
                  {formatSqft(displayProject.sqft)}
                </CitedValue>
              ),
            },
            { label: 'Source', value: displayProject.source },
            {
              label: 'Last activity',
              value: formatDetected(displayProject.detectedAt),
            },
            {
              label: 'Coordinates',
              value: `${formatCoord(displayProject.lat)}, ${formatCoord(displayProject.lng)}`,
            },
          ]}
        />
      </PropertySection>
      <PropertySection title="Team">
        <DetailFieldGrid
          columns={columns}
          fields={[
            { label: 'Developer', value: developerLabel },
            { label: 'Owner', value: ownerLabel },
            { label: 'Architect', value: architectLabel },
            ...contractorField(apiProject?.gc_status, contractorLabel, evidence),
          ]}
        />
      </PropertySection>
      {apiProject ? <ProjectDataSection facts={apiProject} columns={columns} /> : null}
    </>
  );

  const lead = (
    <>
      {detailPending ? (
        <CoverSkeleton size={coverSize} />
      ) : (
        <ProjectHero
          media={apiProject?.media}
          images={visibleImages}
          onImageError={markImageFailed}
          onOpenImage={openImage}
          mapboxToken={import.meta.env.VITE_MAPBOX_TOKEN}
          size={coverSize}
          theme={resolvedTheme}
          stage={displayProject.stage}
          updatesLast30Days={apiProject?.updates_last_30_days ?? 0}
        />
      )}
      <LifecycleRail
        stage={displayProject.stage}
        disposition={displayProject.disposition}
        signals={railSignals.map((s) => ({
          signal_type: s.signal_type,
          filed_at: s.filed_at,
          source_url: s.source_url,
        }))}
      />
    </>
  );

  const overviewContent = overviewPending ? (
    <TextSkeleton />
  ) : projectDescription ? (
    <section className="space-y-2 text-sm">
      <h3 className="font-semibold text-foreground">Description</h3>
      <ExpandableText
        text={projectDescription}
        collapsedLines={mode === 'expanded' ? 6 : 5}
        className="leading-6 text-foreground"
      />
    </section>
  ) : null;

  const signalsContent = (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Signals</h3>
        <SignalScopeToggle scope={signalScope} onScopeChange={setSignalScope} />
      </div>
      <SignalFeed
        signals={signals}
        isLoading={pending(signalsQuery)}
        withheld={signalsQuery.data?.withheld}
        workspaceSlug={workspaceSlug}
      />
    </div>
  );

  // Expanded drops the Overview tab, so the description leads the rail instead.
  const summary =
    mode === 'expanded' ? (
      <>
        {overviewContent}
        {summarySections}
      </>
    ) : (
      summarySections
    );

  return (
    <ImageCacheProvider scope={projectId}>
      <ReportDataIssueDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        workspaceId={workspaceId}
        targetType="project"
        targetId={projectId}
        targetName={displayProject.name}
        buildContext={() =>
          buildReportContext({
            route: pathname,
            project: {
              id: projectId,
              name: displayProject.name,
              type: displayProject.type,
            },
            accounts,
            contacts,
            signals,
          })
        }
      />
      <EntityDetailLayout
        mode={mode}
        header={
          <EntityDrawerHeader
            eyebrow="Project details"
            title={displayProject.name}
            metaIcon={MapPin}
            metaText={`${displayProject.address}, ${displayProject.city}, ${displayProject.state}`}
            chips={
              <>
                <StatusBadge {...stageMeta(displayProject.stage)} />
                <Badge variant="gray">{displayProject.type}</Badge>
              </>
            }
            entityIcon={
              <EntityAvatar
                src={logoUrl(displayProject.sourceLogoDomain)}
                alt={displayProject.name}
                fallbackIcon={getProjectTypeIcon(displayProject.type)}
                href={normalizeExternalUrl(displayProject.sourceBaseUrl)}
              />
            }
            leadingAction={
              <WatchRecordButton workspaceId={workspaceId} dataset="project" recordId={projectId} />
            }
            action={<ReportIssueButton onClick={() => setReportOpen(true)} />}
            externalUrl={getProjectExternalUrl(apiProject, displayProject.sourceBaseUrl)}
            externalLabel={
              apiProject?.project_website_url
                ? 'Open project website'
                : `View source on ${displayProject.source}`
            }
            onPrev={onPrev}
            onNext={onNext}
            mode={mode}
            onToggleMode={onToggleMode}
            backLabel={backLabel}
            onBack={onBack}
          />
        }
        lead={lead}
        summary={summary}
        tabs={[
          { value: 'overview', label: 'Overview', content: overviewContent, drawerOnly: true },
          { value: 'signals', label: 'Signals', content: signalsContent },
          {
            value: 'accounts',
            label: 'Accounts',
            content: pending(accountsQuery) ? (
              <ListSkeleton />
            ) : accountRows.length ? (
              <ExpandableEvidenceList workspaceId={workspaceId} rows={accountRows} />
            ) : (
              <EmptyTabState message="No related accounts" />
            ),
          },
          {
            value: 'contacts',
            label: 'Contacts',
            content: pending(contactsQuery) ? (
              <ListSkeleton />
            ) : contactRows.length ? (
              <ExpandableEvidenceList workspaceId={workspaceId} rows={contactRows} />
            ) : (
              <EmptyTabState message="No related contacts" />
            ),
          },
          {
            value: 'documents',
            label: 'Documents',
            count: documentPages[0]?.total,
            content: (
              <ProjectDocumentsTab
                documents={documents}
                isLoading={pending(documentsQuery) || documentsQuery.isFetchingNextPage}
                isError={documentsQuery.isError}
                hasMore={Boolean(documentsQuery.hasNextPage)}
                onLoadMore={() => void documentsQuery.fetchNextPage()}
              />
            ),
          },
          // Only a project with an image to show gets the tab.
          ...(visibleImages.length > 0
            ? [
                {
                  value: 'photos',
                  label: 'Photos',
                  count: visibleImages.length,
                  content: (
                    <ProjectPhotosGrid
                      images={visibleImages}
                      expanded={mode === 'expanded'}
                      onOpenImage={openImage}
                      onImageError={markImageFailed}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />
      <ImageLightbox
        images={visibleImages}
        index={lightboxIndex >= 0 ? lightboxIndex : null}
        onIndexChange={(index) => setLightboxId(visibleImages[index]?.id ?? null)}
        onClose={() => setLightboxId(null)}
        returnFocusTo={lightboxOpener}
        onImageError={markImageFailed}
      />
    </ImageCacheProvider>
  );
}

type ImageState = { failed: ReadonlySet<string>; open: string | null };
type ImageAction =
  | { type: 'fail'; id: string; ordered: readonly ApiProjectImage[] }
  | { type: 'open'; id: string | null }
  | { type: 'reset' };
const EMPTY_IMAGE_STATE: ImageState = { failed: new Set(), open: null };

/** A failed image on show hands its place to the next usable one after it, else before it. */
function imageStateReducer(state: ImageState, action: ImageAction): ImageState {
  if (action.type === 'reset') return EMPTY_IMAGE_STATE;
  if (action.type === 'open') return { ...state, open: action.id };
  const failed = new Set(state.failed).add(action.id);
  if (state.open !== action.id) return { failed, open: state.open };
  const at = action.ordered.findIndex((image) => image.id === action.id);
  const usable = (image: ApiProjectImage) => !failed.has(image.id);
  const next =
    action.ordered.slice(at + 1).find(usable) ?? action.ordered.slice(0, at).reverse().find(usable);
  return { failed, open: next?.id ?? null };
}

const ACCOUNT_KIND_MAP: Record<ApiProjectAccountType, AccountRow['kind']> = {
  developer: 'developer',
  architect: 'architect',
  engineer: 'engineer',
  gc: 'contractor',
  agency: 'unknown',
  law_firm: 'law_firm',
  consultant: 'consultant',
  landscape_architect: 'landscape_architect',
  other: 'unknown',
};

// Header seed for the navigated account; the detail query fills in the rest.
export function apiAccountToSeedRow(account: ApiProjectAccountSubResource): AccountRow {
  const primary = primaryAccountType(account.account_types);
  return {
    id: account.id,
    name: account.name,
    kind: primary ? (ACCOUNT_KIND_MAP[primary] ?? 'unknown') : 'unknown',
    account_types: account.account_types,
    website: account.website,
    domains: account.domains,
    city: '—',
    state: '—',
    projectsCount: 0,
    contactsCount: 0,
    scores: {},
    lastActivityAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
  };
}

export function projectDisplayRow(
  seed: ProjectRow | null | undefined,
  detail: ApiProject | null | undefined,
): ProjectRow | null {
  return detail ? apiProjectToRow(detail) : (seed ?? null);
}

export function getProjectExternalUrl(
  detail: ApiProject | null | undefined,
  fallbackSourceBaseUrl: string | null | undefined,
): string | undefined {
  return (
    normalizeExternalUrl(detail?.project_website_url) ?? normalizeExternalUrl(fallbackSourceBaseUrl)
  );
}

function formatProjectDescription(value: string | null | undefined): string | null {
  const text = value?.replace(/\s+/g, ' ').trim();
  return text || null;
}

function humanizeRoles(roles: ApiProjectAccountRole[]): string {
  if (!roles.length) return 'Other';
  return roles.map(humanizeRole).join(', ');
}

// The account's effective roles on a project (project_accounts COALESCE: the
// granular specific_role where set, else the coarse role). Coarse roles reuse the
// curated labels; granular specific roles use generic title-casing (MEP fixed up).
const COARSE_PROJECT_ROLES = new Set([
  'developer',
  'owner',
  'architect',
  'gc',
  'engineer',
  'other',
]);

function humanizeProjectRoles(roles: string[]): string {
  return roles.map(humanizeProjectRole).join(', ');
}

function humanizeProjectRole(role: string): string {
  return COARSE_PROJECT_ROLES.has(role)
    ? humanizeRole(role as ApiProjectAccountRole)
    : humanizeSpecificRole(role);
}

function humanizeSpecificRole(role: string): string {
  const label = role.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  return label.replace(/\bMep\b/, 'MEP');
}

function humanizeRole(role: ApiProjectAccountRole): string {
  switch (role) {
    case 'developer':
      return 'Developer';
    case 'owner':
      return 'Owner';
    case 'architect':
      return 'Architect';
    case 'gc':
      return 'General contractor';
    case 'engineer':
      return 'Engineer';
    case 'law_firm':
      return 'Law firm';
    case 'consultant':
      return 'Consultant';
    case 'landscape_architect':
      return 'Landscape architect';
    default:
      return 'Other';
  }
}

function formatAccountNames(
  accounts: ApiProjectAccountSubResource[],
  role: ApiProjectAccountRole,
): string {
  const names = accounts.filter((a) => a.roles.includes(role)).map((a) => a.name);
  return names.join(', ');
}

// A named firm, a filing's "not yet selected" with its source, or no row at all.
function contractorField(
  status: ApiProject['gc_status'] | undefined,
  names: string,
  evidence: NonNullable<ApiProject['field_evidence']>,
): Array<{ label: string; value: React.ReactNode }> {
  if (status === 'named') return [{ label: 'General contractor', value: names }];
  if (status === 'not_yet_selected')
    return [
      {
        label: 'General contractor',
        value: <CitedValue evidence={evidence.gc_status}>Not yet selected</CitedValue>,
      },
    ];
  return [];
}

function formatCoord(value: number): string {
  return Number.isFinite(value) ? value.toFixed(4) : '—';
}
